import React, {useEffect, useMemo, useState} from 'react';
import PropTypes from 'prop-types';
import {useTranslation} from 'react-i18next';
import {Alert, Button, Form} from 'antd';
import {QuestionCircleFilled} from '@ant-design/icons';
import {Collapse} from 'react-collapse';
import {Link} from 'react-router-dom';
import {StyledAddVendorModal} from './StyledAddVendorModal';
import BankingVendorFormItems from '../VendorFormItems/BankingVendorFormItems';
import FullVendorFormItems from '../VendorFormItems/FullVendorFormItems';
import ShortVendorFormItems from '../VendorFormItems/ShortVendorFormItems';
import VatVendorFormItems from '../VendorFormItems/VatVendorFormItems';
import SpinSmall from '../../../SpinSmall';
import {
  elementHelpers,
  formHelpers,
  objectHelpers,
} from '../../../../utils/helpers';
import {vendorPriorityConstants} from '../../../../constants';
import routes from '../../../../routes/routes.json';


const defaultFormValues = {
  address: '',
  bic: '',
  category: [],
  contract_person: '',
  city: '',
  country: undefined,
  email: '',
  iban: '',
  name: '',
  phone_number: '',
  priority: vendorPriorityConstants.TACTICAL,
  vat: ''
};


const AddVendorModal = ({
  dispatch,
  vendorsTags,

  error,
  mode = 'add',
  onCancel,
  onOk,
  loading = false,
  open,
  size = 'full',
  vendor,

  ...rest
}) => {
  const [t] = useTranslation(['main', 'vendors']);
  const [form] = Form.useForm();
  const [initialFormValues, setInitialFormValues] = useState(defaultFormValues);

  const { okButtonText, title } = useMemo(() => {
    const isError = Boolean(error);

    const config = {
      add: {
        okButtonText: isError ? t('tryAgain') : t('create'),
        title: `${t('add')} ${t('new')} ${t('vendor')}`,
      },
      edit: {
        okButtonText: isError ? t('tryAgain') : t('save'),
        title: `${t('edit')} ${t('vendor')}`,
      },
    };

    return config[mode] ?? { okButtonText: undefined, title: undefined };
  }, [error, mode, t]);

  useEffect(() => {
    if (!open) return;

    const getValue = (key, defaultValue = '') => {
      const value = objectHelpers.getObjProp(vendor, key, defaultValue);
      return value === null ? defaultValue : value;
    };

    const getFormValuesFromVendor = () => ({
      address: getValue('address'),
      bic: getValue('bic'),
      category: getValue('category', []),
      contract_person: getValue('contract_person'),
      city: getValue('city'),
      country: getValue('country', undefined),
      email: getValue('email'),
      iban: getValue('iban'),
      name: getValue('name'),
      phone_number: getValue('phone_number'),
      priority: getValue('priority', vendorPriorityConstants.TACTICAL),
      vat: getValue('vat')
    });

    const values = mode === 'edit' ? getFormValuesFromVendor(vendor) : defaultFormValues;

    setInitialFormValues(values);
    form.setFieldsValue(values);
  }, [open, mode, vendor, form]);

  const trans = (key) => t(`vendors:${key}`);

  const formT = (key) => trans(`modal.addVendor.${key}`);

  const handleCreate = () => form.submit();

  const handleCreateWithoutBankingDetails = () => {
    form.setFieldsValue({
      bic: '',
      iban: ''
    });
    handleCreate();
  }

  const footer = [
    (error && mode === 'add') && (
      <Button
        key='create-without-banking-details'
        onClick={handleCreateWithoutBankingDetails}
        size='large'
      >
        {t('createWithoutBankingDetails')}
      </Button>
    ),
    <Button
      key='create'
      onClick={handleCreate}
      size='large'
      type='primary'
    >
      {okButtonText}
    </Button>
  ];

  const handleAfterOpenChange = (open) => {
    if (!open) {
      form.resetFields();
    }
  }

  const handleSubmit = (fields) => {
    if (size === 'short') {
      fields = {
        ...fields,
        priority: vendorPriorityConstants.TACTICAL
      }
    }

    onOk && onOk(
      objectHelpers.trimAllValues(fields),
      null,
      (error) => {
        let fields = formHelpers.getFormServerErrorFields(error.response.data);
        form.setFields(fields);
      }
    )
  }

  const onClose = (e, handleAction = onCancel) => {
    const currentValues = form.getFieldsValue(true);
    const isModified = !objectHelpers.arraysIsEqual(currentValues, initialFormValues);

    if (isModified) {
      elementHelpers.showConfirmModal({
        translate: t,
        onOk: handleAction,
        title: formT(`confirm.${mode}.title`),
        zIndex: 1003
      });
    } else {
      handleAction && handleAction();
    }
  }

  const alertErrorMessage = (
    <>
      <b>{error}</b><br/>
      {t('vendors:error.couldntVefiryBankingDetails')}&nbsp;{t('submitTicketIfIssuePersists')}&nbsp;
      <Link to={routes.support}>
        <QuestionCircleFilled /> {t('section')}
      </Link>
    </>
  );

  return (
    <StyledAddVendorModal
      afterOpenChange={handleAfterOpenChange}
      forceRender
      footer={footer}
      onClose={onClose}
      open={open}
      title={title}
      width={480}
      zIndex={1001}
      {...rest}
    >
      <SpinSmall spinning={loading}>
        <Form
          initialValues={initialFormValues}
          onFinish={handleSubmit}
          layout='vertical'
          form={form}
        >
          {size === 'banking' && <BankingVendorFormItems />}
          {size === 'full' && <FullVendorFormItems initialFormValues={initialFormValues} />}
          {size === 'short' && <ShortVendorFormItems />}
          {size === 'vat' && <VatVendorFormItems />}
        </Form>

        <Collapse isOpened={Boolean(error)}>
          <Alert message={alertErrorMessage} type='warning' showIcon />
        </Collapse>

      </SpinSmall>
    </StyledAddVendorModal>
  );
}

AddVendorModal.propTypes = {
  error: PropTypes.any,
  handleClose: PropTypes.func,
  handleOk: PropTypes.func,
  loading: PropTypes.bool,
  mode: PropTypes.oneOf(['add', 'edit']),
  size: PropTypes.oneOf(['banking', 'full', 'short', 'vat']),
  vendor: PropTypes.object,
}

export default AddVendorModal;
